/**
 * Member Activity feed — the readable notes + unified timeline screen (TRUS-420).
 *
 * Replaces the Activity Log table, whose Notes column was `max-w-xs truncate`, so
 * a DCM could never actually read a note on the page.
 *
 * What this renders:
 *   - every note on the member, newest first, in FULL, in one scrollable list
 *   - author, timestamp and tags on each note
 *   - member/workflow/activity updates in the same list, visually distinct from
 *     notes a DCM wrote (left rail colour + icon + a muted card)
 *   - a category filter, and a composer that tags from the org's own list
 *
 * Note ordering matters in this file: every useState is declared before the
 * hooks that read it. Reading a `const` above its own declaration is a temporal
 * dead zone ReferenceError that renders the whole page blank in the production
 * build only — that is TRUS-418, four days of dead public forms.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FileText, Filter, Loader2, Plus, Save, Settings2, Tag, Trash2, User, X,
  GitBranch, ClipboardList, PencilLine, Paperclip, Download,
  ListChecks, FolderOpen, MessageSquare, Megaphone, Mail, Stethoscope, Share2,
  ExternalLink, ArrowRightLeft, Briefcase, Phone, ChevronDown, ChevronRight,
} from 'lucide-react';

import { Button } from '../ui/button';
import { INK_GHOST_BUTTON } from '../ui/accent';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  memberActivityFeedAPI,
  type ActivityFeedItem,
  type ActivityFeedResponse,
  type FeedCase,
  type FeedCaseFilter,
  type FeedKind,
  type NoteCategory,
  type FeedOrigin,
  type NoteAttachment,
} from '@/services/api/memberActivityFeed';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { getApiErrorMessage } from '@/utils/apiErrors';
import { enrollmentsAPI } from '@/services/api/enrollments';
import { getWeaveinApiUrl } from '@/src/config';
import { formatDateOnly } from '@/utils/dateTime';
import MemberNoteComposer from './MemberNoteComposer';

/**
 * The member's one activity view (TRUS-762) drives the feed through this: the
 * case and kinds to show, the member's cases (for the case chip and Move), and
 * hooks back to the workspace that owns the case bar, chips and Add menu. Absent,
 * the feed behaves exactly as it did on the enrollment page and note sections.
 */
export interface FeedScope {
  caseFilter: FeedCaseFilter;
  /** Kinds to request. null = every kind. */
  types: FeedKind[] | null;
  cases: FeedCase[];
  /** Server's answer (org admins and managers). Never a client-side role guess. */
  canMove: boolean;
  /** New notes are filed under this case. */
  caseEpisodeId?: number | null;
  /** Non-zero opens the note composer (the workspace's Add menu)… */
  composerRequest?: number;
  /** …and this tells the workspace to reset it, so a remount does not reopen it. */
  onComposerOpened?: () => void;
  /** Bump to reload (after something was created elsewhere). */
  refreshKey?: number;
  /** Every response, so the workspace can read counts / cases / can_move. */
  onResponse?: (res: ActivityFeedResponse) => void;
  /** Open a row in its own surface. Only offered when canOpen says so. */
  onOpenItem?: (item: ActivityFeedItem) => void;
  canOpen?: (item: ActivityFeedItem) => boolean;
  /** The org has case management on: draw the case pill and offer Move.
   *  Off, rows carry no case UI at all. */
  showCases?: boolean;
  /** The workspace's filter popover owns the category filter (single: the API
   *  takes one category_id, and a category only exists on notes). */
  categoryId?: number | null;
  /** Inclusive yyyy-mm-dd bounds on occurred_at. */
  dateFrom?: string | null;
  dateTo?: string | null;
  /** Non-zero opens the category manager (the workspace's filter popover). */
  manageCategoriesRequest?: number;
  /** The org's categories and whether this caller may manage them, so the
   *  workspace can offer them without a second request. */
  onCategories?: (categories: NoteCategory[], canManage: boolean) => void;
}

interface MemberActivityFeedProps {
  memberId: number;
  /** Attaches new notes to a specific enrollment; otherwise the API picks the member's current one. */
  enrollmentId?: number;
  readOnly?: boolean;
  scope?: FeedScope;
}

/** Kinds are told apart by icon and label; every row shares one neutral rail. */
const KIND_STYLE: Record<string, { icon: React.ElementType; label: string }> = {
  note:          { icon: FileText,      label: 'Note' },
  member_update: { icon: PencilLine,    label: 'Member update' },
  workflow:      { icon: GitBranch,     label: 'Workflow' },
  activity:      { icon: ClipboardList, label: 'Activity' },
  task:          { icon: ListChecks,    label: 'Task' },
  document:      { icon: FolderOpen,    label: 'Document' },
  form:          { icon: Briefcase,     label: 'Form' },
  message:       { icon: MessageSquare, label: 'Message' },
  outreach:      { icon: Megaphone,     label: 'Outreach' },
  letter:        { icon: Mail,          label: 'Letter' },
  encounter:     { icon: Stethoscope,   label: 'Encounter' },
  referral:      { icon: Share2,        label: 'Referral' },
  call:          { icon: Phone,         label: 'Call' },
};

/** A phone call, whichever kind the API files it under (a `call` kind, or a
 *  message/outreach row whose channel is phone, or one carrying call meta). */
export function isPhoneItem(item: ActivityFeedItem): boolean {
  const m = item.meta || {};
  const channel = String(m.channel || '').toLowerCase();
  return (
    (item.kind as string) === 'call' ||
    channel === 'phone' || channel === 'call' || channel === 'voice' ||
    !!m.call_sid || !!m.recording_url
  );
}

const human = (v: unknown) => String(v ?? '').replace(/_/g, ' ');

function formatSeconds(total: unknown): string | null {
  const n = Number(total);
  if (!Number.isFinite(n) || n <= 0) return null;
  const m = Math.floor(n / 60);
  const s = Math.round(n % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** A recording URL the API returned relative to itself needs the API host. */
function absoluteApiUrl(url: string): string {
  return /^https?:\/\//.test(url) ? url : getWeaveinApiUrl(url);
}

/** i18n key for each kind's singular label; the English is KIND_STYLE's. */
export const KIND_LABEL_KEY: Record<string, string> = {
  note: 'memberWork.kind.note',
  member_update: 'memberWork.kind.member_update',
  workflow: 'memberWork.kind.workflow',
  activity: 'memberWork.kind.activity',
  task: 'memberWork.kind.task',
  document: 'memberWork.kind.document',
  form: 'memberWork.kind.form',
  message: 'memberWork.kind.message',
  outreach: 'memberWork.kind.outreach',
  letter: 'memberWork.kind.letter',
  encounter: 'memberWork.kind.encounter',
  referral: 'memberWork.kind.referral',
  call: 'memberWork.kind.call',
};

/** Same rule the API applies to `case`, for an API that ignored the filter. */
function matchesCase(item: ActivityFeedItem, filter: FeedCaseFilter): boolean {
  if (filter === 'all' || item.case_episode_id === undefined) return true;
  if (filter === 'none') return item.case_episode_id == null;
  return item.case_episode_id === filter;
}

const PAGE_SIZE = 50;
// Matches the API's Query(le=200) ceiling; asking beyond it is a 422.
const MAX_FEED_LIMIT = 200;

/** Feed ids are namespaced ("activity:42") so they cannot collide across sources. */
function activityIdOf(item: ActivityFeedItem): number | null {
  const [kind, id] = (item.id || '').split(':');
  if (kind !== 'activity') return null;
  const n = Number(id);
  return Number.isFinite(n) ? n : null;
}

/**
 * A click that landed on a control inside a row (a button, link, field, the
 * audio player, a menu item — portalled menus bubble through React too) is that
 * control's, not a request to open or close the row.
 */
const ROW_CONTROLS = 'button, a, input, textarea, select, label, audio, video, [role="menuitem"], [role="menu"]';
export function isRowControlClick(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return !!el?.closest?.(ROW_CONTROLS);
}

export default function MemberActivityFeed({
  memberId,
  enrollmentId,
  readOnly = false,
  scope,
}: MemberActivityFeedProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();

  // --- state (all declared before any hook that reads it) -------------------
  const [items, setItems] = useState<ActivityFeedItem[]>([]);
  const [categories, setCategories] = useState<NoteCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<number | null>(null);
  const [originFilter, setOriginFilter] = useState<FeedOrigin | null>(null);
  const [limit, setLimit] = useState(PAGE_SIZE);

  const [composerOpen, setComposerOpen] = useState(false);
  /** note_id currently accepting a file, so only that row shows a spinner. */
  const [attachingNoteId, setAttachingNoteId] = useState<number | null>(null);
  /** attachment_id currently being removed, so only that chip shows a spinner. */
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  const [editCategories, setEditCategories] = useState<number[]>([]);
  // A logged activity is edited in place too. Activities could previously only be
  // created and completed, so a case manager who needed to add detail to a follow-up
  // she had already logged had nowhere to put it (TRUS-509).
  const [editingActivityId, setEditingActivityId] = useState<number | null>(null);

  const [managerOpen, setManagerOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  // Server's answer, not a client-side role guess: authorization is on the ORG
  // role, which the browser does not reliably know.
  const [canManage, setCanManage] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  // Scoped (TRUS-762) paging: older pages are appended by offset, so it is not
  // capped at the API's 200-row limit the way growing `limit` is.
  const [loadingMore, setLoadingMore] = useState(false);
  /** Record id currently being moved, so only that row shows a spinner. */
  const [movingId, setMovingId] = useState<string | null>(null);
  /** Rows opened inline (scoped view): row id → expanded. */
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const loadedCountRef = useRef(0);
  // Every feed request takes a number; only the latest may write rows, counts
  // or errors. Without it a slow response for the previous filter landed after
  // the current one and overwrote it (TRUS-762).
  const requestSeqRef = useRef(0);

  const scoped = !!scope;
  const caseFilter = scope?.caseFilter ?? 'all';
  const typesKey = scope?.types ? scope.types.join(',') : '';
  // Scoped: the workspace's filter popover owns category and dates.
  const effectiveCategory = scoped ? scope?.categoryId ?? null : categoryFilter;
  const dateFrom = scope?.dateFrom ?? null;
  const dateTo = scope?.dateTo ?? null;
  const showCases = scoped && scope?.showCases !== false;
  const onResponseRef = useRef(scope?.onResponse);
  onResponseRef.current = scope?.onResponse;
  const onCategoriesRef = useRef(scope?.onCategories);
  onCategoriesRef.current = scope?.onCategories;

  // --- data ----------------------------------------------------------------
  const loadCategories = useCallback(async () => {
    try {
      const res = await memberActivityFeedAPI.listCategories();
      setCategories(res.items || []);
      setCanManage(Boolean(res.can_manage));
      onCategoriesRef.current?.(res.items || [], Boolean(res.can_manage));
    } catch {
      // Non-fatal: the feed is still readable without the tag picker.
      setCategories([]);
      setCanManage(false);
      onCategoriesRef.current?.([], false);
    }
  }, []);

  // Rows the API should not have sent, for an API that predates `types`/`case`.
  const keepRow = useCallback(
    (item: ActivityFeedItem) =>
      (!typesKey || typesKey.split(',').includes(item.kind)) && matchesCase(item, caseFilter),
    [typesKey, caseFilter],
  );

  const loadFeed = useCallback(async (opts: { keepLoaded?: boolean } = {}) => {
    const seq = ++requestSeqRef.current;
    setLoading(true);
    setError(null);
    try {
      // A refresh after an edit keeps as many rows as were on screen.
      const scopedLimit = opts.keepLoaded
        ? Math.min(Math.max(loadedCountRef.current, PAGE_SIZE), MAX_FEED_LIMIT)
        : PAGE_SIZE;
      const res = await memberActivityFeedAPI.getFeed(memberId, {
        categoryId: effectiveCategory,
        origin: scoped ? null : originFilter,
        limit: scoped ? scopedLimit : limit,
        ...(scoped
          ? { types: typesKey ? (typesKey.split(',') as FeedKind[]) : null, caseFilter, dateFrom, dateTo }
          : {}),
      });
      if (seq !== requestSeqRef.current) return; // a newer request owns the screen
      const rows = scoped ? (res.items || []).filter(keepRow) : res.items || [];
      setItems(rows);
      loadedCountRef.current = (res.items || []).length;
      setHasMore(Boolean(res.has_more));
      onResponseRef.current?.(res);
    } catch (e: any) {
      if (seq !== requestSeqRef.current) return;
      setError(getApiErrorMessage(e) || t('activity.loadFailed', 'Could not load this member’s activity.'));
      setItems([]);
    } finally {
      if (seq === requestSeqRef.current) setLoading(false);
    }
  }, [memberId, effectiveCategory, originFilter, limit, t, scoped, typesKey, caseFilter, dateFrom, dateTo, keepRow]);

  const loadMore = async () => {
    const seq = requestSeqRef.current;
    setLoadingMore(true);
    try {
      const res = await memberActivityFeedAPI.getFeed(memberId, {
        categoryId: effectiveCategory,
        limit: PAGE_SIZE,
        offset: loadedCountRef.current,
        types: typesKey ? (typesKey.split(',') as FeedKind[]) : null,
        caseFilter,
        dateFrom,
        dateTo,
      });
      // The filters changed while this page was in flight: it belongs to the old list.
      if (seq !== requestSeqRef.current) return;
      const page = res.items || [];
      loadedCountRef.current += page.length;
      setItems((prev) => {
        const seen = new Set(prev.map((i) => i.id));
        return [...prev, ...page.filter((i) => !seen.has(i.id) && keepRow(i))];
      });
      setHasMore(Boolean(res.has_more) && page.length > 0);
    } catch (e: any) {
      setError(getApiErrorMessage(e) || t('activity.loadFailed', 'Could not load this member’s activity.'));
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => { loadFeed(); }, [loadFeed]);
  // Something was created outside the feed (the workspace's Add menu).
  // The value it mounted with is not a request: the first load covers it.
  const mountedRefreshKey = useRef(scope?.refreshKey);
  useEffect(() => {
    if (scope?.refreshKey && scope.refreshKey !== mountedRefreshKey.current) {
      mountedRefreshKey.current = scope.refreshKey;
      loadFeed({ keepLoaded: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope?.refreshKey]);
  useEffect(() => {
    if (scope?.manageCategoriesRequest) setManagerOpen(true);
  }, [scope?.manageCategoriesRequest]);
  useEffect(() => {
    if (!scope?.composerRequest) return;
    setComposerOpen(true);
    scope.onComposerOpened?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope?.composerRequest]);

  const activeCategories = useMemo(() => categories.filter((c) => c.is_active), [categories]);

  // --- actions -------------------------------------------------------------
  const toggle = (list: number[], id: number) =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  const beginEdit = (item: ActivityFeedItem) => {
    setEditingNoteId(item.meta?.note_id ?? null);
    setEditText(item.body || '');
    setEditCategories((item.categories || []).map((c) => c.id));
  };

  const saveEdit = async () => {
    if (editingNoteId == null) return;
    setSaving(true);
    try {
      // Sends category_ids explicitly so clearing every tag actually clears them —
      // which is what drops a note out of the Recovery Plan.
      await memberActivityFeedAPI.updateNote(editingNoteId, {
        note_text: editText,
        category_ids: editCategories,
      });
      setEditingNoteId(null);
      await loadFeed({ keepLoaded: true });
    } catch (e: any) {
      setError(getApiErrorMessage(e) || t('activity.saveFailed', 'Could not save the note.'));
    } finally {
      setSaving(false);
    }
  };

  const attachToNote = async (noteId: number, files: FileList | null) => {
    if (!files || files.length === 0) return;
    setAttachingNoteId(noteId);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        await memberActivityFeedAPI.uploadNoteAttachment(noteId, file);
      }
      await loadFeed({ keepLoaded: true });
    } catch (e: any) {
      setError(getApiErrorMessage(e) || t('activity.attachFailed', 'Could not attach that file.'));
    } finally {
      setAttachingNoteId(null);
    }
  };

  // Remove a file already hung off a note — the counterpart to attachToNote. A DCM
  // arranging notes by need has to be able to take a file off the wrong note before
  // attaching it to the right one (TRUS-541). Author-or-admin only, same as editing
  // the note; the backend keeps the stored object, so this only drops the link.
  const removeAttachment = async (noteId: number, attachment: NoteAttachment) => {
    if (!window.confirm(
      t('activity.removeAttachmentConfirm', 'Remove “{name}” from this note?')
        .replace('{name}', attachment.filename),
    )) return;
    setDeletingAttachmentId(attachment.id);
    setError(null);
    try {
      await memberActivityFeedAPI.deleteNoteAttachment(noteId, attachment.id);
      await loadFeed({ keepLoaded: true });
    } catch (e: any) {
      setError(getApiErrorMessage(e) || t('activity.removeAttachmentFailed', 'Could not remove that file.'));
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  /** Streamed through the backend, so it needs the auth header — not a plain link. */
  const downloadAttachment = async (noteId: number, attachment: NoteAttachment) => {
    try {
      const blob = await memberActivityFeedAPI.downloadNoteAttachment(noteId, attachment.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(getApiErrorMessage(e) || t('activity.downloadFailed', 'Could not open that attachment.'));
    }
  };

  const beginEditActivity = (item: ActivityFeedItem) => {
    setEditingActivityId(activityIdOf(item));
    setEditText(item.body || '');
  };

  const saveActivityEdit = async () => {
    if (editingActivityId == null) return;
    setSaving(true);
    try {
      await enrollmentsAPI.updateActivity(editingActivityId, { completion_notes: editText });
      setEditingActivityId(null);
      await loadFeed({ keepLoaded: true });
    } catch (e: any) {
      setError(getApiErrorMessage(e) || t('activity.saveFailed', 'Could not save the activity.'));
    } finally {
      setSaving(false);
    }
  };

  // Re-point a record at another of the member's cases, or at none (TRUS-762).
  const moveToCase = async (item: ActivityFeedItem, target: number | null) => {
    if (!item.record_type || item.record_id == null) return;
    setMovingId(item.id);
    setError(null);
    try {
      await memberActivityFeedAPI.moveRecordCase(memberId, {
        record_type: item.record_type,
        record_id: item.record_id,
        case_episode_id: target,
      });
      await loadFeed({ keepLoaded: true });
    } catch (e: any) {
      setError(getApiErrorMessage(e) || t('memberWork.moveFailed', 'Could not move that record.'));
    } finally {
      setMovingId(null);
    }
  };

  const kindLabel = (kind: string) =>
    t(KIND_LABEL_KEY[kind] || 'memberWork.kind.note', (KIND_STYLE[kind] || KIND_STYLE.note).label);

  const caseLabel = (c: { case_number?: string | null; status?: string | null }) =>
    t('memberWork.caseChip', 'Case #{{number}}', { number: c.case_number || '' });

  const toggleExpanded = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  /** A date-only value ("2026-11-01") or a timestamp, each in its own format. */
  const when = (v: unknown): string | null => {
    if (!v) return null;
    const str = String(v);
    return /^\d{4}-\d{2}-\d{2}$/.test(str) ? formatDateOnly(str) : fmt.dateTime(str);
  };

  /** The row opened in place: what the record says, from its fields and meta. */
  const renderDetails = (item: ActivityFeedItem, phone: boolean) => {
    const m = item.meta || {};
    const rows: Array<[string, React.ReactNode]> = [];
    const add = (key: string, fallback: string, value: React.ReactNode) => {
      if (value !== null && value !== undefined && value !== '') rows.push([t(`memberWork.detail.${key}`, fallback), value]);
    };
    let audio: string | null = null;

    if (phone) {
      add('direction', 'Direction', m.direction ? human(m.direction) : null);
      add('duration', 'Duration', formatSeconds(m.call_duration_seconds ?? m.duration_seconds)
        ?? (m.minutes ? t('memberWork.detail.minutesValue', '{{n}} min', { n: m.minutes }) : null));
      add('status', 'Status', item.status ? human(item.status) : null);
      if (m.recording_url) audio = absoluteApiUrl(String(m.recording_url));
    } else {
      switch (item.kind) {
        case 'task':
          add('status', 'Status', item.status ? human(item.status) : null);
          add('due', 'Due', m.due_at ? fmt.dateTime(m.due_at) : when(m.due_date));
          add('priority', 'Priority', m.priority ? human(m.priority) : null);
          add('assignee', 'Assigned to', m.assigned_to_name ?? null);
          add('completedAt', 'Completed', when(m.completed_at));
          break;
        case 'form':
          add('template', 'Form', m.form_template_name ?? null);
          add('status', 'Status', item.status ? human(item.status) : null);
          add('completion', 'Completion', m.completion_percentage != null ? `${Math.round(Number(m.completion_percentage))}%` : null);
          add('submittedAt', 'Submitted', when(m.submitted_at));
          add('completedBy', 'Completed by', m.completed_by_name ?? null);
          break;
        case 'outreach':
          add('channel', 'Channel', m.channel ? human(m.channel) : null);
          add('direction', 'Direction', m.direction ? human(m.direction) : null);
          add('minutes', 'Minutes', m.minutes != null && Number(m.minutes) > 0 ? String(m.minutes) : null);
          add('location', 'Location', m.location ?? null);
          break;
        case 'letter':
          add('mailType', 'Mail type', m.mail_type ? human(m.mail_type) : null);
          add('status', 'Status', item.status ? human(item.status) : null);
          add('expectedDelivery', 'Expected delivery', when(m.expected_delivery_date));
          add('deliveredAt', 'Delivered', when(m.delivered_at));
          add('tracking', 'Tracking', m.tracking_url ? (
            <a href={String(m.tracking_url)} target="_blank" rel="noopener noreferrer" className="text-v2accent hover:underline">
              {t('memberWork.detail.trackLetter', 'Track this letter')}
            </a>
          ) : null);
          break;
        case 'message':
          add('channel', 'Channel', m.channel ? human(m.channel) : null);
          add('direction', 'Direction', m.direction ? human(m.direction) : null);
          add('status', 'Status', item.status ? human(item.status) : null);
          break;
        case 'document':
          add('docType', 'Type', m.doc_type_name ?? m.document_type ?? null);
          add('verification', 'Verification', m.verification_status ? human(m.verification_status) : m.verified != null ? (m.verified ? t('memberWork.detail.verified', 'Verified') : t('memberWork.detail.notVerified', 'Not verified')) : null);
          add('effective', 'Effective', when(m.effective_date));
          add('expires', 'Expires', when(m.expiration_date));
          break;
        case 'encounter':
          add('encounterType', 'Type', m.encounter_type ? human(m.encounter_type) : null);
          add('minutes', 'Minutes', m.duration_minutes ?? null);
          add('units', 'Units', m.units ?? null);
          add('validation', 'Validation', m.validation_status ? human(m.validation_status) : null);
          break;
        case 'referral':
          add('referralNumber', 'Referral #', m.referral_number ?? null);
          add('urgency', 'Urgency', m.urgency_level ? human(m.urgency_level) : null);
          add('provider', 'Provider', m.selected_provider_name ?? m.assigned_to_cbo_name ?? null);
          add('acceptance', 'Acceptance', m.acceptance_status ? human(m.acceptance_status) : null);
          break;
        case 'workflow':
          add('progress', 'Progress', m.progress_percentage != null ? `${Math.round(Number(m.progress_percentage))}%` : null);
          add('steps', 'Steps', m.total_steps ? `${m.completed_steps ?? 0} / ${m.total_steps}` : null);
          break;
        case 'activity':
          add('activityType', 'Type', m.activity_type ? human(m.activity_type) : null);
          add('status', 'Status', item.status ? human(item.status) : null);
          break;
        default:
          break;
      }
    }

    return (
      <div className="mt-2 rounded-md border border-v2border bg-surface p-3 space-y-3">
        {rows.length > 0 && (
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2">
            {rows.map(([label, value]) => (
              <div key={label} className="min-w-0">
                <dt className="text-[11px] text-ink-500">{label}</dt>
                <dd className="text-sm text-ink break-words">{value}</dd>
              </div>
            ))}
          </dl>
        )}
        {audio && (
          <div>
            <div className="text-[11px] text-ink-500 mb-1">{t('memberWork.detail.recording', 'Recording')}</div>
            <audio controls preload="none" src={audio} className="w-full max-w-md">
              {t('memberWork.detail.noAudio', 'Your browser cannot play this recording.')}
            </audio>
          </div>
        )}
        {item.body ? (
          <p className="text-sm text-ink whitespace-pre-wrap leading-relaxed break-words">{item.body}</p>
        ) : rows.length === 0 && !audio ? (
          <p className="text-sm text-ink-500">{t('memberWork.detail.nothingMore', 'No more details on this record.')}</p>
        ) : null}
      </div>
    );
  };

  const addCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) return;
    try {
      await memberActivityFeedAPI.createCategory(name);
      setNewCategoryName('');
      await loadCategories();
    } catch (e: any) {
      setError(getApiErrorMessage(e) || t('activity.categoryFailed', 'Could not add that category.'));
    }
  };

  const removeCategory = async (id: number) => {
    try {
      await memberActivityFeedAPI.deleteCategory(id);
      await loadCategories();
      if (categoryFilter === id) setCategoryFilter(null);
    } catch (e: any) {
      setError(getApiErrorMessage(e) || t('activity.categoryFailed', 'Could not remove that category.'));
    }
  };

  // --- render --------------------------------------------------------------
  const chip = (c: NoteCategory) => (
    <span
      key={c.id}
      className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium border"
      style={{
        color: c.color || undefined,
        borderColor: c.color ? `${c.color}55` : undefined,
        backgroundColor: c.color ? `${c.color}14` : undefined,
      }}
    >
      <Tag className="w-2.5 h-2.5" />
      {c.name}
    </span>
  );

  return (
    <div className="space-y-4">
      {/* Toolbar: filter by category, split notes vs system, add a note. In the
          scoped view the workspace owns kinds and Add, so only categories stay —
          and nothing at all when the org has none to filter by. */}
      {!scoped && (
      <div className="flex flex-wrap items-center gap-2">
        <Filter className="w-4 h-4 text-gray-400" />
        <button
          type="button"
          onClick={() => setCategoryFilter(null)}
          className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
            categoryFilter === null
              ? 'bg-v2accent text-white border-v2accent'
              : 'border-v2border bg-surface text-ink-500 hover:text-ink'
          }`}
        >
          {t('activity.allCategories', 'All')}
        </button>
        {activeCategories.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => {
              const next = categoryFilter === c.id ? null : c.id;
              setCategoryFilter(next);
              // A category exists only on notes; leaving origin=system set would
              // return nothing, and the disabled control could not undo it.
              if (next !== null) setOriginFilter(null);
            }}
            className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
              categoryFilter === c.id
                ? 'bg-v2accent text-white border-v2accent'
                : 'border-v2border bg-surface text-ink-500 hover:text-ink'
            }`}
          >
            {c.name}
          </button>
        ))}

        {!scoped && <span className="mx-1 h-4 w-px bg-gray-300 dark:bg-gray-600" />}

        {/* Notes vs system. Disabled while a category filter is on, because a
            category only exists on notes — the combination has no meaning. */}
        {!scoped && (
        <select
          value={originFilter ?? ''}
          onChange={(e) => setOriginFilter((e.target.value || null) as FeedOrigin | null)}
          disabled={categoryFilter !== null}
          className="text-xs rounded-md border border-v2border bg-surface px-2 py-1 disabled:opacity-50"
          aria-label={t('activity.filterOrigin', 'Filter by source')}
        >
          <option value="">{t('activity.everything', 'Everything')}</option>
          <option value="note">{t('activity.notesOnly', 'Notes only')}</option>
          <option value="system">{t('activity.updatesOnly', 'Updates only')}</option>
        </select>
        )}

        <div className="ml-auto flex items-center gap-2">
          {canManage && (
            <Button size="sm" variant="outline" onClick={() => setManagerOpen((v) => !v)}>
              <Settings2 className="w-4 h-4 mr-1.5" />
              {t('activity.manageCategories', 'Categories')}
            </Button>
          )}
          {!readOnly && !scoped && (
            <Button size="sm" onClick={() => setComposerOpen((v) => !v)}>
              <Plus className="w-4 h-4 mr-1.5" />
              {t('activity.addNote', 'Add note')}
            </Button>
          )}
        </div>
      </div>
      )}

      {/* Admin: edit the org's own category list */}
      {managerOpen && canManage && (
        <div className="rounded-lg border border-v2border bg-surface p-3 space-y-2">
          <p className="text-xs text-gray-500">
            {t('activity.categoriesHelp', 'These categories belong to your organization. Removing one hides it from the picker; notes already tagged keep the tag.')}
          </p>
          <div className="flex flex-wrap gap-2">
            {categories.filter((c) => c.is_active).map((c) => (
              <span key={c.id} className="inline-flex items-center gap-1.5 rounded border border-v2border px-2 py-1 text-xs">
                {c.name}
                {typeof c.note_count === 'number' && c.note_count > 0 && (
                  <span className="text-gray-400">({c.note_count})</span>
                )}
                <button
                  type="button"
                  onClick={() => removeCategory(c.id)}
                  aria-label={`${t('activity.removeCategory', 'Remove category')}: ${c.name}`}
                  className="text-gray-400 hover:text-red-600"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex items-center gap-2 pt-1">
            <input
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addCategory(); }}
              placeholder={t('activity.newCategory', 'New category name')}
              className="flex-1 max-w-xs text-sm rounded-md border border-v2border bg-surface px-2 py-1.5"
            />
            <Button size="sm" variant="outline" onClick={addCategory} disabled={!newCategoryName.trim()}>
              {t('common.add', 'Add')}
            </Button>
          </div>
        </div>
      )}

      {/* Composer (the same one the Activities workspace opens as a screen). */}
      {composerOpen && !readOnly && (
        <MemberNoteComposer
          memberId={memberId}
          enrollmentId={enrollmentId}
          caseEpisodeId={scope?.caseEpisodeId}
          categories={categories}
          onSaved={() => {
            setComposerOpen(false);
            void loadFeed({ keepLoaded: true });
          }}
          onCancel={() => setComposerOpen(false)}
        />
      )}

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/30 dark:border-red-900 dark:text-red-300">
          {error}
        </div>
      )}

      {/* The feed */}
      {loading ? (
        <div className="flex items-center justify-center gap-2 py-10 text-gray-400">
          <Loader2 className="w-4 h-4 animate-spin" /> {t('activity.loading', 'Loading activity…')}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-10 text-gray-400">
          <p className="text-sm">{t('activity.empty', 'Nothing on this member yet.')}</p>
          {!scoped && categoryFilter !== null && (
            <button type="button" className="text-xs underline mt-1" onClick={() => setCategoryFilter(null)}>
              {t('activity.clearFilter', 'Clear the category filter')}
            </button>
          )}
        </div>
      ) : (
        <ol className="space-y-2">
          {items.map((item) => {
            const phone = isPhoneItem(item);
            const style = phone ? KIND_STYLE.call : KIND_STYLE[item.kind] || KIND_STYLE.note;
            const Icon = style.icon;
            const isNote = item.origin === 'note';
            const editing = isNote && editingNoteId === item.meta?.note_id;
            const noteId: number | null = isNote ? item.meta?.note_id ?? null : null;
            const attachments: NoteAttachment[] = (item.meta?.attachments as NoteAttachment[]) || [];
            const activityId = activityIdOf(item);
            const editingActivity = activityId !== null && editingActivityId === activityId;
            // TRUS-762 fields: only drawn when the API sent them, and only when
            // the org has case management.
            const hasCaseField = showCases && item.case_episode_id !== undefined;
            const programName: string | null = item.program_name ?? item.meta?.program_name ?? null;
            const openable = !!scope?.onOpenItem && (scope.canOpen ? scope.canOpen(item) : true);
            const movable =
              showCases && !!scope?.canMove && !readOnly && !!item.record_type && item.record_id != null;
            // Scoped rows open in place; notes are already shown in full.
            const expandable = scoped && !isNote;
            const isOpen = expandable && expanded.has(item.id);
            const rowLabel = phone ? kindLabel('call') : kindLabel(item.kind);
            return (
              <li
                key={item.id}
                data-feed-row={item.id}
                // TRUS-762: the WHOLE row opens and closes its details, not just
                // its title. Clicks on the row's own controls (Open, Move, Edit,
                // attachments, the recording player) and text selection are left
                // to them.
                onClick={
                  expandable
                    ? (e) => {
                        if (isRowControlClick(e.target)) return;
                        if (typeof window !== 'undefined' && window.getSelection?.()?.toString()) return;
                        toggleExpanded(item.id);
                      }
                    : undefined
                }
                className={`relative rounded-lg border pl-4 pr-3 py-3 ${
                  isNote
                    ? 'border-v2border bg-surface'
                    : 'border-dashed border-v2border bg-paper-2/60'
                } ${expandable ? 'cursor-pointer hover:border-ink-400' : ''}`}
              >
                {/* One neutral rail; the icon says what the row is. */}
                <span className="absolute left-0 top-0 bottom-0 w-1 rounded-l-lg bg-v2border" aria-hidden="true" />

                <div className="flex items-start gap-2">
                  <Icon className="w-4 h-4 mt-0.5 shrink-0 text-ink-500" aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                      {expandable ? (
                        <button
                          type="button"
                          onClick={() => toggleExpanded(item.id)}
                          aria-expanded={isOpen}
                          className="inline-flex items-center gap-1 text-sm font-medium text-ink text-left hover:text-v2accent"
                        >
                          {isOpen ? <ChevronDown className="w-3.5 h-3.5 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 shrink-0" />}
                          {item.title || rowLabel}
                        </button>
                      ) : (
                        <span className="text-sm font-medium text-ink">
                          {item.title || (isNote ? t('activity.note', 'Note') : scoped ? rowLabel : style.label)}
                        </span>
                      )}
                      {!isNote && (
                        <span className="inline-flex items-center rounded border border-v2border px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-ink-500">
                          {scoped ? rowLabel : style.label}
                        </span>
                      )}
                      {scoped && item.status && (
                        <span className="inline-flex items-center rounded border border-v2border px-1.5 py-0.5 text-[10px] text-ink-500">
                          {human(item.status)}
                        </span>
                      )}
                      <span className="text-xs text-ink-400 inline-flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {item.author_name || t('activity.system', 'System')}
                      </span>
                      <span className="text-xs text-ink-400">
                        {item.occurred_at ? fmt.dateTime(item.occurred_at) : ''}
                      </span>
                      {item.meta?.edited && (
                        <span className="text-xs text-ink-400 italic">{t('activity.edited', 'edited')}</span>
                      )}
                      {/* The full flow for this record, always on the row's first
                          line so it cannot be missed (TRUS-762). */}
                      {openable && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => scope?.onOpenItem?.(item)}
                          className="ml-auto !border !border-v2border !text-ink hover:!bg-paper-2 !px-2 !py-0.5 !text-xs"
                        >
                          <ExternalLink className="w-3 h-3 mr-1" />
                          {item.kind === 'message' ? t('memberWork.openInMessages', 'Open in messages') : t('memberWork.open', 'Open')}
                        </Button>
                      )}
                    </div>

                    {/* Which case (or none) and program the record belongs to, plus
                        the open / move actions (TRUS-762). */}
                    {scoped && (hasCaseField || programName || movable) && (
                      <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px]">
                        {hasCaseField && (
                          <span className="inline-flex items-center rounded px-1.5 py-0.5 border border-v2border text-ink-500">
                            {item.case_episode_id != null
                              ? caseLabel({ case_number: item.case_number })
                              : t('memberWork.noCase', 'No case')}
                          </span>
                        )}
                        {programName && <span className="text-ink-500">{programName}</span>}
                        {movable && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                disabled={movingId === item.id}
                                className={`${INK_GHOST_BUTTON} !px-1.5 !py-0.5 !text-[11px]`}
                              >
                                {movingId === item.id ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <ArrowRightLeft className="w-3 h-3" />
                                )}
                                {t('memberWork.moveToCase', 'Move to case')}
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start">
                              <DropdownMenuLabel>{t('memberWork.moveToCase', 'Move to case')}</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              {(scope?.cases || [])
                                .filter((c) => c.id !== item.case_episode_id)
                                .map((c) => (
                                  <DropdownMenuItem key={c.id} onSelect={() => void moveToCase(item, c.id)}>
                                    {caseLabel(c)}
                                    <span className="ml-2 text-xs text-ink-400">
                                      {[c.program_name, c.status].filter(Boolean).join(' · ')}
                                    </span>
                                  </DropdownMenuItem>
                                ))}
                              {item.case_episode_id != null && (
                                <DropdownMenuItem onSelect={() => void moveToCase(item, null)}>
                                  {t('memberWork.noCase', 'No case')}
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    )}

                    {isOpen && renderDetails(item, phone)}

                    {/* The whole point: the note body, in full, no truncation. */}
                    {editing ? (
                      <div className="mt-2 space-y-2">
                        <textarea
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          rows={5}
                          className="w-full text-sm rounded-md border border-v2border bg-surface px-2 py-1.5 resize-y min-h-[120px] leading-relaxed"
                        />
                        <div className="flex flex-wrap gap-1.5">
                          {activeCategories.map((c) => (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() => setEditCategories((prev) => toggle(prev, c.id))}
                              aria-pressed={editCategories.includes(c.id)}
                              className={`px-2 py-1 text-xs rounded border ${
                                editCategories.includes(c.id)
                                  ? 'bg-v2accent text-white border-v2accent'
                                  : 'border-v2border text-ink-500'
                              }`}
                            >
                              {c.name}
                            </button>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={saveEdit} disabled={saving}>
                            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                            {t('common.save', 'Save')}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingNoteId(null)}>
                            <X className="w-4 h-4 mr-1.5" />{t('common.cancel', 'Cancel')}
                          </Button>
                        </div>
                      </div>
                    ) : editingActivity ? (
                      <div className="mt-2 space-y-2">
                        <textarea
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          rows={5}
                          className="w-full text-sm rounded-md border border-v2border bg-surface px-2 py-1.5 resize-y min-h-[120px] leading-relaxed"
                          placeholder={t('activity.activityNotes', 'Notes on this activity')}
                        />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={saveActivityEdit} disabled={saving}>
                            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                            {t('common.save', 'Save')}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingActivityId(null)}>
                            <X className="w-4 h-4 mr-1.5" />{t('common.cancel', 'Cancel')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      // Expanded rows show the body in their details panel. A
                      // collapsed non-note row in the scoped view previews two lines.
                      item.body && !isOpen && (
                        <p
                          className={`mt-1.5 text-sm text-ink whitespace-pre-wrap leading-relaxed break-words ${
                            expandable ? 'line-clamp-2' : ''
                          }`}
                        >
                          {item.body}
                        </p>
                      )
                    )}

                    {/* Files hung off the note. Rendered as download chips rather than
                        plain links: the stream needs the auth header (TRUS-520). */}
                    {(attachments.length > 0 || (isNote && item.editable && !readOnly)) && (
                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        {attachments.map((a) => {
                          const canDelete = isNote && item.editable && !readOnly && noteId !== null;
                          return (
                            <span
                              key={a.id}
                              className="inline-flex items-center rounded border border-v2border text-xs text-ink-500 max-w-[264px] overflow-hidden"
                            >
                              <button
                                type="button"
                                onClick={() => noteId !== null && downloadAttachment(noteId, a)}
                                title={a.filename}
                                className="inline-flex items-center gap-1 px-2 py-0.5 hover:text-v2accent max-w-[240px]"
                              >
                                <Download className="w-3 h-3 shrink-0" />
                                <span className="truncate">{a.filename}</span>
                              </button>
                              {canDelete && (
                                <button
                                  type="button"
                                  onClick={() => removeAttachment(noteId as number, a)}
                                  disabled={deletingAttachmentId === a.id}
                                  aria-label={`${t('activity.removeFile', 'Remove file')}: ${a.filename}`}
                                  title={t('activity.removeFile', 'Remove file')}
                                  className="flex items-center border-l border-v2border px-1 py-0.5 text-gray-400 hover:text-red-600 disabled:opacity-50"
                                >
                                  {deletingAttachmentId === a.id ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <X className="w-3 h-3" />
                                  )}
                                </button>
                              )}
                            </span>
                          );
                        })}
                        {isNote && item.editable && !readOnly && noteId !== null && (
                          <label className="inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-v2accent cursor-pointer">
                            {attachingNoteId === noteId ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Paperclip className="w-3 h-3" />
                            )}
                            {t('activity.attachFile', 'Attach a file')}
                            <input
                              type="file"
                              multiple
                              className="hidden"
                              disabled={attachingNoteId === noteId}
                              onChange={(e) => {
                                void attachToNote(noteId, e.target.files);
                                e.target.value = '';
                              }}
                            />
                          </label>
                        )}
                      </div>
                    )}

                    {(item.categories?.length > 0 ||
                      (isNote && item.editable && !editing) ||
                      (activityId !== null && item.editable && !editingActivity)) && (
                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        {item.categories?.map(chip)}
                        {isNote && item.editable && !editing && !readOnly && (
                          <button
                            type="button"
                            onClick={() => beginEdit(item)}
                            className="text-[11px] text-gray-400 hover:text-v2accent underline ml-1"
                          >
                            {t('common.edit', 'Edit')}
                          </button>
                        )}
                        {/* Same affordance on a logged activity the caller may correct —
                            the backend marks it editable for the author and for admins,
                            and never once it has been billed (TRUS-509). */}
                        {activityId !== null && item.editable && !editingActivity && !readOnly && (
                          <button
                            type="button"
                            onClick={() => beginEditActivity(item)}
                            className="text-[11px] text-gray-400 hover:text-v2accent underline ml-1"
                          >
                            {t('common.edit', 'Edit')}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {/* Paginate on has_more. The API caps limit at 200, so stop offering the
          control at the ceiling rather than sending 250 and 422-ing the feed. */}
      {scoped && !loading && items.length > 0 && hasMore && (
        <div className="flex justify-center">
          <Button size="sm" variant="outline" onClick={loadMore} disabled={loadingMore}>
            {loadingMore && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
            {t('activity.loadMore', 'Show older')}
          </Button>
        </div>
      )}
      {!scoped && !loading && items.length > 0 && hasMore && limit < MAX_FEED_LIMIT && (
        <div className="flex justify-center">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setLimit((l) => Math.min(l + PAGE_SIZE, MAX_FEED_LIMIT))}
          >
            {t('activity.loadMore', 'Show older')}
          </Button>
        </div>
      )}
    </div>
  );
}
